import {
  addDoc,
  collection,
  doc,
  endAt,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAt,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { tratarErroDeCallable } from '@/lib/erros';
import { etapaPorId } from '@/lib/constants';
import { normalizarTelefone, variantesDaBusca, variantesTelefone } from '@/lib/telefone';
import { validarEtapa } from '@/leads/regras';
import { paraNumero as paraNumeroBr } from '@/leads/recebimentos';
import { camposDoComparecimento } from '@/leads/comparecimento';
import { exigeMotivo, motivoValido, textoDaCorrecao, validarCorrecao, validarEntrada } from '@/leads/valores';
import { dadosDaTarefaDeRetorno } from '@/leads/retorno';
import {
  comitarComConclusoes,
  concluirAbertasNoLote,
  lerAbertasDoLead,
  montarTarefa,
} from '@/services/tarefasService';
import { abertasAFechar, sufixoDaConclusao } from '@/leads/conclusaoAutomatica';
import { autoria as camposDeAutoria } from '@/leads/autoria';
import { comResgate } from '@/leads/resgate';
import { copiasDesatualizadas, dividirEmLotes } from '@/leads/copiasDoNome';
import { consultasParaBaixar, statusDaBaixa } from '@/agenda/baixa';
import { buscarAgendamentosDoLead } from '@/services/agendamentosService';
import { tratarErro } from '@/lib/erros';

// Reexportados para que a UI importe tudo do serviço, sem precisar saber onde
// mora cada peça.
export { normalizarTelefone, formatarTelefone } from '@/lib/telefone';
export { validarEtapa, exigeCampoExtra } from '@/leads/regras';

const colecaoLeads = (clinicaId) => collection(db, 'clinicas', clinicaId, 'leads');
const refLead = (clinicaId, leadId) => doc(db, 'clinicas', clinicaId, 'leads', leadId);
const colecaoInteracoes = (clinicaId, leadId) =>
  collection(db, 'clinicas', clinicaId, 'leads', leadId, 'interacoes');

/**
 * Ouve todos os leads da clínica em tempo real.
 *
 * A Fase 1 carrega a coleção inteira e filtra no cliente: mantém kanban e
 * lista sempre coerentes entre si e evita uma combinatória de índices para
 * cada cruzamento de filtro. Quando uma clínica passar da casa dos milhares de
 * leads, trocar por paginação com query no servidor.
 */
export function ouvirLeads(clinicaId, aoReceber, aoFalhar) {
  const q = query(colecaoLeads(clinicaId), orderBy('atualizadoEm', 'desc'));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('os leads', aoFalhar),
  );
}

export function ouvirTimeline(clinicaId, leadId, aoReceber, aoFalhar) {
  const q = query(colecaoInteracoes(clinicaId, leadId), orderBy('criadoEm', 'desc'), limit(100));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('a linha do tempo', aoFalhar),
  );
}

/**
 * ★★ WO — a autoria mora em `@/leads/autoria`, e é o LUGAR ÚNICO.
 *
 * ⚠ Antes era local deste arquivo, e `tarefasService` e `agendamentosService` montavam o
 * mesmo trio à mão — 10 escritas que a regra nova de `clinicaId` recusaria. Guarda de fonte
 * em `wo.autoria.guardas.test.js` cobra que ninguém volte a montar à mão.
 */
const autoria = (autor, clinicaId, lead = null) =>
  camposDeAutoria(autor, { clinicaId, lead, carimbo: serverTimestamp });

/**
 * Campos do SDR ORIGINAL (V5): quem recebeu o lead e quando. Só entram quando
 * há um SDR; quem chama garante que o lead ainda não tem original.
 */
const carimboOriginal = (sdrId, sdrNome) =>
  sdrId
    ? { sdrOriginalId: sdrId, sdrOriginalNome: sdrNome ?? '', sdrOriginalEm: serverTimestamp() }
    : {};

/**
 * Atribui (ou desatribui) o SDR de um lead que está em memória.
 *
 * A primeira atribuição carimba `sdrOriginalId/Nome/Em`; as seguintes trocam só
 * `sdrId/Nome` — o crédito de leads trabalhados, agendamentos e comparecimentos
 * fica com quem recebeu o lead. Desatribuir (`sdr` nulo) nunca toca o original.
 */
/**
 * ★★★ PC-6 — a troca de SDR passa pela callable `assumirLead`. ⚠ As rules fecham `sdrId`/
 * `sdrOriginal*`/`posse*` a QUALQUER cliente (inclusive o gestor): é a callable que aplica a trava
 * (`situacaoDePosse`), move as tarefas abertas da dona anterior e grava a trilha num commit.
 * A assinatura continua a de antes, para a tela não mudar de forma. `autor` não vai: o servidor
 * sabe quem chamou.
 */
// WT lote 1: toda callable passa por um `chamarCallable` (registra o `code` do servidor e relança).
const chamarCallable = (nome) => async (dados) => {
  try {
    return await httpsCallable(functions, nome)(dados);
  } catch (erro) {
    throw tratarErroDeCallable(nome, erro);
  }
};
const chamarAssumirLead = async (dados) => (await chamarCallable('assumirLead')(dados))?.data;

/** Gestor: atribui (ou desatribui, `sdr` nulo) o SDR de um lead. */
// eslint-disable-next-line no-unused-vars
export async function atribuirSdr(clinicaId, lead, sdr, autor) {
  return chamarAssumirLead({ clinicaId, leadId: lead.id, paraId: sdr?.id ?? '' });
}

/** SDR: assume o lead para si (a callable recusa com o texto da trava se não puder). */
export async function assumirLeadParaMim(clinicaId, lead) {
  return chamarAssumirLead({ clinicaId, leadId: lead.id });
}

/**
 * Lead pelo telefone, nas DUAS grafias do celular (com e sem o nono dígito) —
 * a mesma regra do servidor (`repositorio.js:buscarLeadPorTelefone`).
 *
 * Antes era igualdade exata: lead cadastrado à mão com o 9 e busca pelo número
 * como o JID entrega (sem o 9) não se achavam, e o `criarLead` duplicava.
 */
export async function buscarPorTelefone(clinicaId, telefone) {
  const e164 = normalizarTelefone(telefone);
  if (!e164) return null;
  const variantes = variantesTelefone(e164);
  if (!variantes.length) return null;
  const snap = await getDocs(
    query(colecaoLeads(clinicaId), where('telefone', 'in', variantes), limit(1)),
  );
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

/**
 * Busca de leads para "Vincular a lead existente" no painel da conversa.
 *
 * Mesmo desenho da busca de conversas (`conversasService.buscarConversas`):
 * telefone por igualdade nas variantes (o caminho confiável) e nome por PREFIXO,
 * com e sem a primeira letra maiúscula. Deduplicado por id. Fora do
 * `LeadsProvider` não há a coleção em memória, então a busca vai ao banco.
 */
/**
 * ★★ WI — LÊ UM lead por id. Existe para a AGENDA, que vive FORA do `LeadsProvider`.
 *
 * O diálogo da baixa "Compareceu" precisa do lead para pré-preencher código e nome — sem
 * isso a SDR redigita o que o sistema já sabe, que é o desenho que a WH mediu e recusou.
 * ★ Custo: UM `getDoc` por baixa (≈7/dia em setembro, ≈15/dia em julho). A ficha
 * (`BlocoAgendamento`) não paga: ela já tem o lead em memória e o passa em `opcoes.lead`.
 *
 * ⚠ Só quem é `podeVendas` alcança esta leitura — as rules reservam `leads`. O botão que a
 * dispara está atrás de `podeVendas`, e há guarda de fonte vigiando isso.
 */
export async function buscarLead(clinicaId, leadId) {
  const s = await getDoc(refLead(clinicaId, leadId));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

export async function buscarLeads(clinicaId, termo) {
  const texto = String(termo ?? '').trim();
  if (texto.length < 3) return [];

  const consultas = [];
  const variantes = variantesDaBusca(texto);
  if (variantes.length) {
    consultas.push(
      getDocs(query(colecaoLeads(clinicaId), where('telefone', 'in', variantes), limit(20))),
    );
  }

  const porPrefixo = (t) =>
    getDocs(query(colecaoLeads(clinicaId), orderBy('nome'), startAt(t), endAt(`${t}\uf8ff`), limit(20)));
  const capitalizado = texto.charAt(0).toUpperCase() + texto.slice(1);
  consultas.push(porPrefixo(texto));
  if (capitalizado !== texto) consultas.push(porPrefixo(capitalizado));

  /**
   * ★★★ WT lote 1 — O RESULTADO DIZ SE ESTÁ COMPLETO.
   *
   * ⚠⚠ Era `continue` com um `console.error`: uma das consultas podia ser **recusada** e a
   * busca devolvia a outra como se fosse tudo. ★ Na tela: **menos resultados, sem erro
   * nenhum** — a assinatura exata do defeito da peça WC, onde a recusa virava ausência.
   *
   * ★ Agora a lista carrega `incompleta` e a tela avisa. ⚠ Devolver `[]` ou lançar seria
   * pior: a pessoa perderia o que DEU para achar.
   */
  const resultados = await Promise.allSettled(consultas);
  const achados = new Map();
  let incompleta = false;
  for (const r of resultados) {
    if (r.status !== 'fulfilled') {
      incompleta = true;
      tratarErro('busca de leads', null, 'service')(r.reason);
      continue;
    }
    for (const d of r.value.docs) achados.set(d.id, { id: d.id, ...d.data() });
  }
  const lista = [...achados.values()];
  // ★ marca no ARRAY, não num objeto novo: todo chamador já trata isto como lista, e
  // trocar a forma do retorno quebraria os cinco pontos que a consomem.
  lista.incompleta = incompleta;
  return lista;
}

/**
 * Cria um lead. Telefone duplicado não gera lead novo: registra a nova origem
 * na timeline do lead existente e devolve `duplicado` (PLANO 4.3).
 */
export async function criarLead(clinicaId, dados, autor) {
  const telefone = normalizarTelefone(dados.telefone);
  const erro = validarEtapa(dados.etapa ?? 'novo', dados);
  if (erro) throw new Error(erro);
  // VH: lead criado À MÃO tem origem obrigatória. O vazio só é legítimo no lead
  // que nasce de mensagem sem sinal (servidor) — aqui a pessoa sabe de onde veio.
  if (!String(dados.origem ?? '').trim()) throw new Error('Escolha a origem do lead.');

  if (telefone) {
    const existente = await buscarPorTelefone(clinicaId, telefone);
    if (existente) {
      await addDoc(colecaoInteracoes(clinicaId, existente.id), {
        tipo: 'criacao',
        texto: `Novo contato deste telefone via ${dados.origem ?? 'origem não informada'} — lead já existente, timeline unificada.`,
        ...autoria(autor, clinicaId, existente),
      });
      return { id: existente.id, duplicado: true, lead: existente };
    }
  }

  const agora = serverTimestamp();
  const lead = {
    nome: String(dados.nome ?? '').trim(),
    telefone,
    email: String(dados.email ?? '').trim(),
    cidade: String(dados.cidade ?? '').trim(),
    origem: String(dados.origem).trim(),
    procedimento: dados.procedimento ?? '',
    etapa: dados.etapa ?? 'novo',
    temperatura: dados.temperatura ?? 'morno',
    sdrId: dados.sdrId ?? autor?.id ?? '',
    sdrNome: dados.sdrNome ?? autor?.nome ?? '',
    // V5: quem RECEBEU o lead. Gravado na primeira atribuição e nunca mais — é
    // quem leva o crédito de leads trabalhados/agendamentos/comparecimentos,
    // mesmo que o lead troque de SDR depois. Lead criado já com SDR nasce com o
    // original preenchido; sem SDR (resgate, callable), fica vazio até a
    // primeira atribuição (`atribuirSdr`/`atribuirEmMassa`/`atualizarLead`).
    ...carimboOriginal(dados.sdrId ?? autor?.id ?? '', dados.sdrNome ?? autor?.nome ?? ''),
    observacoes: String(dados.observacoes ?? '').trim(),
    tags: dados.tags ?? [],
    tentativasContato: 0,
    clinicaId,
    criadoEm: agora,
    atualizadoEm: agora,
  };
  if (dados.valorFechado) lead.valorFechado = Number(dados.valorFechado);

  const ref = await addDoc(colecaoLeads(clinicaId), lead);
  await addDoc(colecaoInteracoes(clinicaId, ref.id), {
    tipo: 'criacao',
    texto: `Lead criado via ${lead.origem}.`,
    ...autoria(autor, clinicaId, lead),
  });
  return { id: ref.id, duplicado: false };
}

export async function atualizarLead(clinicaId, leadId, campos, autor) {
  const dados = { ...campos, atualizadoEm: serverTimestamp() };
  if (dados.telefone !== undefined) dados.telefone = normalizarTelefone(dados.telefone);
  /**
   * ★★★ PC-6 — a POSSE não passa por aqui. Era a defesa V5 para quem atribuía SDR por este caminho
   * (nenhum chamador o usava — conferido); desde as rules da PC-6 a gravação seria recusada de
   * qualquer jeito. ★ Falha ALTO, com o caminho certo, em vez de um "sem permissão" longe da causa.
   */
  const POSSE = ['sdrId', 'sdrNome', 'sdrOriginalId', 'sdrOriginalNome', 'sdrOriginalEm', 'posseReservadaAte',
    'posseConfirmadaEm', 'sdrAnteriorId', 'sdrAnteriorNome', 'sdrAssumidoPorId', 'sdrAssumidoEm'];
  if (POSSE.some((k) => k in dados)) {
    throw new Error('A SDR do lead muda só por "Assumir lead" / "SDR responsável" (atribuirSdr).');
  }
  /**
   * VE — o orçamento preenchido DEPOIS (painel do lead ou da conversa) carimba
   * `valorOrcadoEm` na primeira vez, pela mesma razão do `sdrOriginalEm`: a data
   * do orçamento é o dia em que a oportunidade nasceu, e não pode andar a cada
   * correção de valor.
   *
   * ★★ WD-c1 (25/set/2026) — razão do dono, com todas as letras: **"a data da oportunidade é a data em que a
   * pessoa COMPARECEU, não a data em que alguém digitou o valor depois"**.
   *
   * Isto é o que impede a oportunidade de MIGRAR DE MÊS no relatório. `metas/calculo.js:152,166` conta a
   * oportunidade do mês por `valorOrcadoEm`; se o carimbo fosse refeito a cada gravação, corrigir hoje um
   * valor orçado em julho tiraria o número de julho e o jogaria em setembro. **Só a primeira vez. Se já
   * existir, não se mexe.** Medido em produção (25/set): 0 leads têm `valorOrcado` sem o carimbo, então
   * nenhum caso hoje migraria — e é justamente esta regra que mantém assim.
   */
  if (dados.valorOrcado !== undefined) {
    dados.valorOrcado = Math.max(0, paraNumeroBr(dados.valorOrcado));
    const atual = (await getDoc(refLead(clinicaId, leadId))).data();
    if (dados.valorOrcado > 0 && !atual?.valorOrcadoEm) dados.valorOrcadoEm = serverTimestamp();
  }
  await updateDoc(refLead(clinicaId, leadId), dados);
  if (campos.registrarNaTimeline !== false) {
    await addDoc(colecaoInteracoes(clinicaId, leadId), {
      tipo: 'nota',
      texto: 'Dados do lead atualizados.',
      ...autoria(autor, clinicaId),
    });
  }
}

/**
 * Move o lead de etapa e registra a mudança na timeline — as duas escritas no
 * mesmo lote, para que o histórico nunca fique dessincronizado do estado.
 */
export async function moverEtapa(clinicaId, lead, novaEtapa, extras = {}, autor) {
  if (lead.etapa === novaEtapa) return;
  const erro = validarEtapa(novaEtapa, { ...lead, ...extras });
  if (erro) throw new Error(erro);

  /**
   * ★★ WE (a) — A CONSULTA ABERTA RECEBE BAIXA JUNTO COM A ETAPA.
   *
   * Até esta peça, `moverEtapa` não tocava a agenda — nenhuma linha. Mover o lead para "Não fechou",
   * "Fechou" ou "Não compareceu" deixava a consulta em `agendado` **para sempre**, e a pessoa tinha de ir
   * fechá-la à mão depois. Fechando à mão, ela caía no defeito (b): a baixa puxava a etapa de volta.
   * Passivo medido no dia do conserto: **71 leads** em etapa pós-comparecimento com avaliação aberta.
   *
   * Quem decide o status é `@/agenda/baixa` — puro, com as três decisões escritas lá: quais etapas
   * significam que a pessoa veio (incluindo `fechado`), que só `avaliacao` recebe baixa, e que são TODAS
   * as abertas, não a mais recente.
   *
   * ★ Leitura ANTES do lote, como manda o arquivo. A consulta é a que já existe
   * (`buscarAgendamentosDoLead`), coberta pelo índice `leadId + dataHora` já declarado — nenhum índice novo.
   */
  const statusBaixa = statusDaBaixa(novaEtapa);

  /**
   * ★ VJ — a DATA DE RETORNO das colunas de "não" vira TAREFA no MESMO lote: é o
   * "próximo contato" que Próximos contatos e o painel já leem. Só quando a
   * tela mandou `retornoEm` (motivo vivo — `validarEtapa` já exigiu); motivo
   * que encerra não tem retorno. Se o lead já tinha tarefa pendente, esta entra
   * assim mesmo (decisão do dono): a data escolhida agora é a que vale.
   *
   * ★★ PC-2 (#2) — montada ANTES da leitura: o responsável dela é o critério de quais
   * abertas o gesto encerra (decisão 3), e o id dela vai em `excetoIds`.
   */
  const retornoEm = extras.retornoEm instanceof Date ? extras.retornoEm : null;
  const tarefaRetorno = retornoEm
    ? montarTarefa(
      clinicaId,
      lead,
      dadosDaTarefaDeRetorno(lead, {
        etapa: novaEtapa,
        motivoRotulo: extras.motivoRotulo,
        retornoEm,
        negociadorId: extras.negociadorId,
        negociadorNome: extras.negociadorNome,
      }),
      autor,
    )
    : null;
  const refTarefaRetorno = tarefaRetorno
    ? doc(collection(db, 'clinicas', clinicaId, 'leads', lead.id, 'tarefas'))
    : null;

  /**
   * ★★ PC-2 (F1/#2) — FECHAR encerra TODAS as abertas; o retorno das colunas de "não"
   * encerra as do MESMO responsável. A leitura vai no MESMO `Promise.all` da das consultas,
   * ANTES do lote, e é do servidor (`lerAbertasDoLead`).
   */
  const motivoConclusao = novaEtapa === 'fechado' ? 'fechou' : tarefaRetorno ? 'novo_contato' : null;
  const [consultasDoLead, leituraAbertas] = await Promise.all([
    statusBaixa ? buscarAgendamentosDoLead(clinicaId, lead.id) : [],
    motivoConclusao ? lerAbertasDoLead(clinicaId, lead.id) : { abertas: [] },
  ]);
  const aBaixar = statusBaixa ? consultasParaBaixar(consultasDoLead) : [];
  const aFechar = motivoConclusao
    ? abertasAFechar(leituraAbertas.abertas, {
      motivo: motivoConclusao,
      responsavelNovo: tarefaRetorno?.responsavelId,
      sdrDoLead: lead.sdrId,
      excetoIds: refTarefaRetorno ? [refTarefaRetorno.id] : [],
    })
    : [];

  const montar = (fechar) => {
    const lote = writeBatch(db);
    const mudancas = { etapa: novaEtapa, atualizadoEm: serverTimestamp() };
    if (extras.valorFechado !== undefined) mudancas.valorFechado = Number(extras.valorFechado);
    /**
     * VG — o MOTIVO das duas colunas de "não", e se ele ENCERRA.
     *
     * `encerra` vem decidido pelo diálogo, a partir da lista da clínica, e é
     * GRAVADO aqui como `desfechoNegativoEm` — nunca inferido depois pela config.
     * Mover para qualquer etapa que não encerra LIMPA o carimbo: quem volta para
     * "Agendado" deixou de ser desfecho.
     */
    if (extras.motivoNaoAgendou !== undefined) {
      mudancas.motivoNaoAgendou = extras.motivoNaoAgendou;
      mudancas.motivoNaoAgendouEm = serverTimestamp();
    }
    if (extras.motivoNaoFechou !== undefined) {
      mudancas.motivoNaoFechou = extras.motivoNaoFechou;
      mudancas.motivoNaoFechouEm = serverTimestamp();
    }
    mudancas.desfechoNegativoEm = extras.encerra === true ? serverTimestamp() : null;
    // V4: o negociador escolhido no diálogo entra junto com a etapa (compareceu /
    // em_negociacao), como o valor entra com "fechado".
    if (extras.negociadorId !== undefined) {
      mudancas.negociadorId = extras.negociadorId || null;
      mudancas.negociadorNome = extras.negociadorNome ?? '';
    }
    /**
     * VA — o fechamento também registra o que o paciente pagou NO ATO e como
     * encontrá-lo no Careflow.
     *
     * `valorEntrada` é campo do LEAD, não recebimento: a entrada reaparece na
     * planilha do Careflow como parcela liquidada, e contá-la nos dois lugares
     * somaria o mesmo dinheiro duas vezes na comissão. Como é update em `leads`,
     * o SDR pode gravá-lo — não há escrita em `recebimentos` no fechamento.
     *
     * `codigoCareflow` digitado aqui é o que faz a importação (VA-2) casar por
     * código; o nome vira fallback só para os leads fechados antes desta peça.
     */
    if (extras.valorEntrada !== undefined) {
      mudancas.valorEntrada = Math.max(0, paraNumeroBr(extras.valorEntrada));
    }
    /**
     * ★★ WI (29/set/2026) — OS CAMPOS DO COMPARECIMENTO SAÍRAM DAQUI.
     *
     * Eles moram em `@/leads/comparecimento` (`camposDoComparecimento`, puro), porque a WI
     * abriu um SEGUNDO caminho para o mesmo fato: a baixa "Compareceu" pela AGENDA e pela
     * FICHA, que grava por `registrarComparecimento`. ★ Copiar estava proibido, e com razão:
     * a marca `semOrcamento` tem uma LIMPEZA (valor > 0 derruba a marca) que, divergindo,
     * faria o contador `compareceuSemOrcamento` das Metas mentir para sempre.
     *
     * ★ `serverTimestamp` vai como PARÂMETRO — é o que mantém o módulo puro e testável sem
     * emulador. E os campos entram no MESMO lote da etapa, nunca depois: não pode existir um
     * instante em que o lead esteja em `compareceu` sem os dados, porque é exatamente o
     * estado que a porta (`validarEtapa`) e as rules recusam.
     *
     * ★ `nomeCompleto` NÃO é do comparecimento (é o nome do contrato, da VA) e por isso fica.
     */
    Object.assign(mudancas, camposDoComparecimento(lead, extras, {
      novaEtapa,
      autor,
      carimbo: serverTimestamp,
      // ★★ PC-5 — a MESMA leitura da baixa (acima, no `Promise.all`), feita ANTES do lote:
      // a avaliação que este lote baixa ainda está `agendado` aqui, e não conta como prova.
      // ⚠ Só é lida quando há `statusBaixa`; `fechado` sempre tem (`agenda/baixa.js`).
      agendamentosAntes: consultasDoLead,
    }));
    if (extras.nomeCompleto !== undefined) {
      mudancas.nomeCompleto = String(extras.nomeCompleto ?? '').trim();
    }
    // VR: fechou em mês posterior ao de criadoEm → resgate no mesmo lote (data da ação = agora). As colunas de "não" não realocam.
    /**
     * ★★ WO — QUEM FECHOU fica no LEAD, não só na timeline.
     *
     * Medido na `wo-medicao.md` §2(d): `dataFechamento` é `serverTimestamp()` (confiável), mas
     * **o lead não guardava autor nenhum** — ele existia só na interação `mudanca_etapa`. Sem
     * o campo, "quem fechou hoje" obriga a garimpar a timeline de cada lead.
     */
    if (novaEtapa === 'fechado') {
      Object.assign(mudancas, {
        dataFechamento: serverTimestamp(),
        fechadoPorId: autor?.id ?? 'sistema',
        fechadoPorNome: autor?.nome ?? 'Sistema',
      }, comResgate(lead, new Date()) ?? {});
    }
    lote.update(refLead(clinicaId, lead.id), mudancas);

    // ★ WE (a): as consultas abertas entram no MESMO lote — ou muda a etapa e elas fecham, ou nada acontece.
    for (const a of aBaixar) {
      lote.update(doc(db, 'clinicas', clinicaId, 'agendamentos', a.id), {
        status: statusBaixa,
        atualizadoEm: serverTimestamp(),
        resolvidoPorId: autor?.id ?? 'sistema',
        resolvidoPorNome: autor?.nome ?? 'Sistema',
      });
    }

    // ★ VJ: a tarefa de retorno (montada antes da leitura, acima) entra no MESMO lote.
    if (tarefaRetorno) lote.set(refTarefaRetorno, tarefaRetorno);
    if (motivoConclusao) concluirAbertasNoLote(lote, clinicaId, lead.id, fechar, motivoConclusao, autor);

    const de = etapaPorId(lead.etapa).label;
    const para = etapaPorId(novaEtapa).label;
    const retornoTexto = retornoEm
      ? ` — retorno em ${retornoEm.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
      : '';
    const complemento =
      novaEtapa === 'fechado'
        ? ` — valor ${Number(extras.valorFechado).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
        : extras.motivoRotulo
          ? ` — motivo: ${extras.motivoRotulo}${extras.encerra === true ? ' (encerra)' : ''}${retornoTexto}`
          : retornoTexto;
    /**
     * ★ WE (a): a baixa entra como SUFIXO da linha que já existe — UMA linha, não N.
     * A timeline é append-only e o fato é um só: "movi a etapa, e as consultas acompanharam".
     */
    const rotuloBaixa = { compareceu: 'comparecida', faltou: 'como falta', cancelado: 'cancelada' }[statusBaixa];
    const textoBaixa = aBaixar.length
      ? ` — ${aBaixar.length === 1 ? 'consulta' : `${aBaixar.length} consultas`} ${rotuloBaixa}`
      : '';
    lote.set(doc(colecaoInteracoes(clinicaId, lead.id)), {
      tipo: 'mudanca_etapa',
      texto: `${de} → ${para}${complemento}${textoBaixa}${sufixoDaConclusao(fechar.length)}`,
      de: lead.etapa,
      para: novaEtapa,
      ...autoria(autor, clinicaId, lead),
    });
    return lote;
  };

  await comitarComConclusoes(montar, aFechar);
}

export async function adicionarNota(clinicaId, leadId, texto, autor) {
  const conteudo = String(texto ?? '').trim();
  if (!conteudo) return;
  const lote = writeBatch(db);
  lote.set(doc(colecaoInteracoes(clinicaId, leadId)), {
    tipo: 'nota',
    texto: conteudo,
    ...autoria(autor, clinicaId),
  });
  lote.update(refLead(clinicaId, leadId), { atualizadoEm: serverTimestamp() });
  await lote.commit();
}

/**
 * ★ VM — renomeia o lead pela ficha.
 *
 * Quem manda no CRM é `lead.nome` (Funil, Lista, CSV, metas). Para a mesma
 * pessoa não ter dois nomes na tela, o `contatoNomeSalvo` das conversas de
 * VENDAS vinculadas a este lead vai junto, no MESMO lote; conversas de outro
 * espaço ficam de fora — o SDR não tem permissão nelas, e um lote é tudo ou
 * nada. A timeline registra "Nome alterado de X para Y". Vazio é recusado
 * (lead sem nome não existe); igual ao atual não grava nada. O chat continua
 * editando só a conversa (decisão do dono; pendência registrada no ESTADO).
 */
export async function renomearLead(clinicaId, lead, novoNome, autor) {
  const nome = String(novoNome ?? '').trim();
  if (!nome) throw new Error('Informe o nome do lead.');
  const anterior = String(lead?.nome ?? '').trim();
  if (nome === anterior) return { renomeado: false };
  /**
   * ★ WC — o filtro de `espaco` NÃO é otimização: é o que torna a consulta permitida.
   *
   * A `allow list` de `conversas` decide pela FORMA DA CONSULTA — `doEspacoDaConversa()` lê
   * `resource.data.espaco`, e numa listagem esse campo só existe para o motor se a própria consulta o
   * restringir. Sem ele, toda SDR e toda recepcionista levam `permission-denied` (medido com a coleção
   * VAZIA: a recusa não depende de documento). O gestor passava porque `isGestor()` é a primeira cláusula
   * do `||` e curto-circuita antes de tocar `resource.data` — por isso o defeito nunca apareceu para quem
   * administra. Ver `docs/wc-medicao.md`.
   *
   * `'vendas'` é o espaço certo aqui: o lote só escreve `contatoNomeSalvo` nas conversas de vendas (as
   * outras o `PainelContato` trata à parte), então pedir mais do que isso seria pedir o que não se usa.
   */
  /**
   * ★★ Nome do lead (28/out) — as CÓPIAS `leadNome` dos agendamentos e das tarefas vão junto
   * (`leads/copiasDoNome.js`). Todas as do lead, inclusive de consulta passada e tarefa concluída: a busca
   * da Agenda e o histórico leem a cópia. Rules: agendamento é escrita de vendas e recepção, tarefa é de
   * vendas — quem renomeia (vendas) pode as duas, seja de quem for a consulta ou a tarefa.
   */
  const [vinculadas, agendamentos, tarefas] = await Promise.all([
    getDocs(query(
      collection(db, 'clinicas', clinicaId, 'conversas'),
      where('espaco', '==', 'vendas'),
      where('leadId', '==', lead.id),
    )),
    getDocs(query(collection(db, 'clinicas', clinicaId, 'agendamentos'), where('leadId', '==', lead.id))),
    getDocs(query(collection(db, 'clinicas', clinicaId, 'leads', lead.id, 'tarefas'))),
  ]);
  const conversas = vinculadas.docs.filter((c) => c.data()?.espaco === 'vendas');
  const copias = copiasDesatualizadas([...agendamentos.docs, ...tarefas.docs], nome);
  const [doPrimeiro, ...seguintes] = dividirEmLotes(2 + conversas.length, copias);

  const lote = writeBatch(db);
  lote.update(refLead(clinicaId, lead.id), { nome, atualizadoEm: serverTimestamp() });
  for (const c of conversas) lote.update(c.ref, { contatoNomeSalvo: nome });
  lote.set(doc(colecaoInteracoes(clinicaId, lead.id)), {
    tipo: 'nota',
    texto: `Nome alterado de ${anterior || '(sem nome)'} para ${nome}`,
    ...autoria(autor, clinicaId, lead),
  });
  for (const c of doPrimeiro) lote.update(c.ref, { leadNome: nome });
  await lote.commit();
  for (const grupo of seguintes) {
    const mais = writeBatch(db);
    for (const c of grupo) mais.update(c.ref, { leadNome: nome });
    await mais.commit();
  }
  return { renomeado: true, conversas: conversas.length, copias: copias.length };
}

/**
 * ★ WD — corrige `valorFechado` e `valorEntrada` de um lead JÁ fechado.
 *
 * Existe porque não havia caminho: os dois campos só eram escritos pelo diálogo de mudança de etapa, e
 * `moverEtapa` sai cedo quando a etapa não muda (medido em `docs/wd-medicao.md`). Não é permissão — é
 * ausência de caminho, e é isto que a preenche.
 *
 * ★ MOTIVO OBRIGATÓRIO (decisão do dono), e a recusa é AQUI, antes de montar o lote: validação que vive só
 * no componente é validação que o próximo chamador não tem.
 *
 * Um lote, tudo ou nada: o lead e a linha da timeline com o DE-PARA. Sem os dois juntos, existiria correção
 * de dinheiro sem rastro — que é justamente o que a peça não pode permitir.
 *
 * ★ A comissão NÃO é afetada: a folha lê `compareceuEm`/agendamentos e `recebimento.valor` (medido). O que
 * muda são os RELATÓRIOS, inclusive de meses passados, porque metas/dashboard/placar recalculam na leitura.
 */
export async function corrigirValores(clinicaId, lead, valores, autor) {
  const { valorFechado, valorEntrada, valorOrcado, codigoCareflow, nome, motivo } = valores ?? {};

  /**
   * ★★ WD-c2 — CAMPO AUSENTE significa "não mexi nele", NUNCA "zere". Sem esta distinção, um chamador que
   * mandasse só `{ valorFechado, motivo }` apagaria a entrada, a oportunidade e o código do lead, porque
   * `undefined` vira 0 no parser. O diálogo manda sempre os cinco; a regra existe para o PRÓXIMO chamador.
   */
  const veio = (campo) => valores != null && valores[campo] !== undefined;
  const totalAlvo = veio('valorFechado') ? valorFechado : lead?.valorFechado;

  const erroTotal = validarCorrecao({ etapa: lead?.etapa, valorFechado: totalAlvo });
  if (erroTotal) throw new Error(erroTotal);
  if (veio('valorEntrada')) {
    const erroEntrada = validarEntrada({ valorFechado: totalAlvo, valorEntrada });
    if (erroEntrada) throw new Error(erroEntrada);
  }
  // ★ WD-c2: o motivo é exigido SÓ quando o total muda (e havia total antes). A regra está em
  // `leads/valores.js`; aqui só se obedece — validação que vive no componente o próximo chamador não tem.
  const pedeMotivo = exigeMotivo({ valorFechadoAnterior: lead?.valorFechado, valorFechadoNovo: totalAlvo });
  if (pedeMotivo && !motivoValido(motivo)) throw new Error('Escreva o motivo da correção do total.');

  const mudancas = {};
  const num = (v) => Math.max(0, paraNumeroBr(v));
  if (veio('valorFechado') && num(valorFechado) !== paraNumeroBr(lead?.valorFechado)) mudancas.valorFechado = num(valorFechado);
  if (veio('valorEntrada') && num(valorEntrada) !== paraNumeroBr(lead?.valorEntrada)) mudancas.valorEntrada = num(valorEntrada);
  if (veio('valorOrcado') && num(valorOrcado) !== paraNumeroBr(lead?.valorOrcado)) mudancas.valorOrcado = num(valorOrcado);
  if (veio('codigoCareflow') && String(codigoCareflow ?? '').trim() !== (lead?.codigoCareflow ?? '')) {
    mudancas.codigoCareflow = String(codigoCareflow ?? '').trim();
  }
  const nomeNovo = String(nome ?? '').trim();
  const renomeou = veio('nome') && nomeNovo && nomeNovo !== (lead?.nome ?? '').trim();

  if (Object.keys(mudancas).length === 0 && !renomeou) return { corrigido: false };

  if (Object.keys(mudancas).length > 0) {
    /**
     * ★ `valorOrcadoEm` carimbado só na PRIMEIRA vez — a data da oportunidade é a do comparecimento, não a
     * de quem digitou depois. Mesma regra do `atualizarLead` e do `moverEtapa`.
     */
    if (mudancas.valorOrcado > 0 && !lead?.valorOrcadoEm) mudancas.valorOrcadoEm = serverTimestamp();

    const lote = writeBatch(db);
    lote.update(refLead(clinicaId, lead.id), { ...mudancas, atualizadoEm: serverTimestamp() });
    // ★ A timeline registra o TOTAL. Os operacionais mudam sem linha — é a decisão da assimetria.
    if ('valorFechado' in mudancas) {
      lote.set(doc(colecaoInteracoes(clinicaId, lead.id)), {
        tipo: 'nota',
        texto: textoDaCorrecao(lead, mudancas, motivo),
        ...autoria(autor, clinicaId, lead),
      });
    }
    await lote.commit();
  }

  // ★ O nome vai por CAMINHO PRÓPRIO: `renomearLead` renomeia também as conversas de vendas vinculadas (VM).
  //   Limite declarado: são duas escritas, não uma transação.
  if (renomeou) await renomearLead(clinicaId, lead, nomeNovo, autor);

  return { corrigido: true, mudancas, renomeou };
}

/** Registra uma tentativa de contato — alimenta o contador da cadência (PLANO 4.3). */
export async function registrarTentativa(clinicaId, lead, resultado, autor) {
  const lote = writeBatch(db);
  lote.update(refLead(clinicaId, lead.id), {
    tentativasContato: (lead.tentativasContato ?? 0) + 1,
    ultimaTentativaEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
  lote.set(doc(colecaoInteracoes(clinicaId, lead.id)), {
    tipo: 'ligacao',
    texto: `Tentativa de contato — ${resultado}.`,
    // ★ WO — o resultado vira CAMPO. Medido: contar 'atendidas' dependia de
    // `texto.includes('atendeu')`, que quebra se alguém reescrever a frase.
    resultado,
    ...autoria(autor, clinicaId, lead),
  });
  await lote.commit();
}

/**
 * Ações em massa da visão em lista (PLANO 4.4).
 * Cada lead custa 2 escritas (lead + timeline) e um batch do Firestore aceita
 * 500, então a seleção é quebrada em blocos de 200 leads.
 */
const LEADS_POR_LOTE = 200;

/** ★ PC-6: em blocos de 100 (o teto da callable), pelo servidor. */
export const LEADS_POR_CHAMADA_POSSE = 100;

// eslint-disable-next-line no-unused-vars
export async function atribuirEmMassa(clinicaId, leads, sdr, autor) {
  const resultados = [];
  for (let i = 0; i < leads.length; i += LEADS_POR_CHAMADA_POSSE) {
    const bloco = leads.slice(i, i + LEADS_POR_CHAMADA_POSSE);
    const r = await chamarAssumirLead({ clinicaId, leadIds: bloco.map((l) => l.id), paraId: sdr?.id ?? '' });
    resultados.push(...(r?.resultados ?? []));
  }
  return { resultados };
}

/**
 * ★★ PC-2 (A3) — teto de escritas de UM lote no mover em massa.
 *
 * ⚠ O Firestore aceita 500 escritas por lote. Antes eram 200 leads × 2 escritas (400); com
 * até N conclusões por lead ao mover para `agendado`, contar LEADS deixaria o lote passar de
 * 500 e o commit inteiro falharia. O corte passa a ser por ESCRITAS, com folga.
 */
export const ESCRITAS_POR_LOTE = 450;

/** Agrupa os leads em blocos cujo custo (2 + conclusões de cada um) não passa do teto. PURO. */
export function blocosPorEscritas(leads, custoDoLead, teto = ESCRITAS_POR_LOTE) {
  const blocos = [];
  let atual = [];
  let escritas = 0;
  for (const lead of leads ?? []) {
    const custo = custoDoLead(lead);
    if (atual.length && escritas + custo > teto) {
      blocos.push(atual);
      atual = [];
      escritas = 0;
    }
    atual.push(lead);
    escritas += custo;
  }
  if (atual.length) blocos.push(atual);
  return blocos;
}

/** Muda a etapa de vários leads de uma vez. Só etapas que não exigem campo extra. */
export async function moverEtapaEmMassa(clinicaId, leads, novaEtapa, autor) {
  const erro = validarEtapa(novaEtapa, {});
  if (erro) throw new Error(`${erro} Use o painel do lead para esta etapa.`);

  const alvos = leads.filter((l) => l.etapa !== novaEtapa);

  /**
   * ★★ PC-2 (A3) — mover para `agendado` encerra as abertas de cada lead. Os `alvos` já
   * excluem quem está na etapa, então é SEMPRE transição. ⚠ `fechado` não chega aqui:
   * `validarEtapa('fechado', {})` recusa. Leituras ANTES dos lotes, em paralelo por bloco.
   */
  const aFecharPorLead = new Map();
  if (novaEtapa === 'agendado') {
    for (let i = 0; i < alvos.length; i += LEADS_POR_LOTE) {
      const bloco = alvos.slice(i, i + LEADS_POR_LOTE);
      const leituras = await Promise.all(bloco.map((l) => lerAbertasDoLead(clinicaId, l.id)));
      bloco.forEach((l, k) => aFecharPorLead.set(l.id, abertasAFechar(leituras[k].abertas, { motivo: 'agendou' })));
    }
  }

  const blocos = blocosPorEscritas(alvos, (l) => 2 + (aFecharPorLead.get(l.id)?.length ?? 0));
  for (const bloco of blocos) {
    const aFechar = bloco.flatMap((l) => aFecharPorLead.get(l.id) ?? []);
    const montar = (fechar) => {
      const comConclusao = fechar.length > 0;
      const lote = writeBatch(db);
      for (const lead of bloco) {
        const doLead = comConclusao ? (aFecharPorLead.get(lead.id) ?? []) : [];
        lote.update(refLead(clinicaId, lead.id), {
          etapa: novaEtapa,
          atualizadoEm: serverTimestamp(),
        });
        lote.set(doc(colecaoInteracoes(clinicaId, lead.id)), {
          tipo: 'mudanca_etapa',
          texto: `${etapaPorId(lead.etapa).label} → ${etapaPorId(novaEtapa).label}${sufixoDaConclusao(doLead.length)}`,
          de: lead.etapa,
          para: novaEtapa,
          ...autoria(autor, clinicaId, lead),
        });
        concluirAbertasNoLote(lote, clinicaId, lead.id, doLead, 'agendou', autor);
      }
      return lote;
    };
    await comitarComConclusoes(montar, aFechar);
  }
  return alvos.length;
}
